import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  Building2,
  ExternalLink,
  Link2,
  Loader2,
  Mail,
  MessageSquareText,
  NotebookPen,
  Pencil,
  Plus,
  Reply,
  RefreshCw,
  Search,
  UserRound,
} from 'lucide-react';
import type { Client, Task, User as UserEntity, Website } from '@oneup/shared';
import {
  clientsApi,
  communicationsApi,
  tasksApi,
  type CommunicationRecordDetails,
  type CommunicationTimelineItem,
  type CommunicationTimelineSource,
  type GmailIntegrationStatus,
  usersApi,
  websitesApi,
} from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUiStore } from '@/stores/ui';
import {
  DetailGroup,
  DetailRow,
  DetailsActionBar,
  DetailsDrawer,
  DetailsDrawerBody,
  DetailsPanel,
  DetailsPanelBody,
  JsonPreview,
} from '@/components/details-drawer';
import { contextItem, pageContext, suggestion, useAlicePageContext } from '@/components/alice';
import { cn } from '@/lib/utils';
import { adminConfig } from '@/platform/config';

type SourceFilter = 'all' | CommunicationTimelineSource;

const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: 'all', label: 'All sources' },
  { value: 'quick_note', label: 'Quick Notes' },
  { value: 'gmail', label: 'Gmail' },
  { value: 'activity', label: 'Send Updates' },
];

export function CommunicationsView() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<SourceFilter>('all');
  const [selectedItem, setSelectedItem] = useState<CommunicationTimelineItem | null>(null);
  const trimmedSearch = search.trim();
  const setNotice = useUiStore((state) => state.setNotice);

  const { data, isLoading, error } = useQuery({
    queryKey: ['communications', source, trimmedSearch],
    queryFn: () =>
      communicationsApi.list({
        source,
        search: trimmedSearch || undefined,
        limit: 100,
      }),
    refetchInterval: 60_000,
  });
  const gmailStatusQuery = useQuery({
    queryKey: ['communications', 'gmail-status'],
    queryFn: communicationsApi.gmailStatus,
    staleTime: 15_000,
  });
  const gmailStatus = gmailStatusQuery.data;
  const syncBlockedReason = gmailSyncBlockedReason(gmailStatus, gmailStatusQuery.isLoading);

  const items = data?.items ?? [];
  const communicationId = searchParams.get('communicationId');
  const aliceContext = useMemo(
    () =>
      pageContext({
        portalKey: adminConfig.appId,
        pageKey: 'communications',
        pageTitle: 'Communications',
        recordType: selectedItem ? 'communication' : undefined,
        recordId: selectedItem?.recordId,
        recordLabel: selectedItem?.title,
        organizationId: selectedItem?.clientId,
        organizationLabel: selectedItem?.clientName ?? selectedItem?.clientId,
        contextItems: [
          contextItem(
            'communications-page',
            'page',
            'Communications timeline',
            'Client messages from Quick Notes, Gmail, and outgoing activity.',
          ),
          ...(selectedItem
            ? [
                contextItem(
                  selectedItem.id,
                  'communication',
                  selectedItem.title,
                  selectedItem.summary ?? selectedItem.sourceLabel,
                ),
              ]
            : []),
        ],
        suggestedActions: selectedItem
          ? [
              suggestion('communication-draft-reply', 'Draft reply', 'Start a reply draft from this communication.', 'composer'),
              suggestion('communication-actions', 'Pull action items', 'Extract decisions and follow-ups from the visible thread.'),
              suggestion('communication-task', 'Update related task', 'Suggest task updates based on this communication.'),
            ]
          : [],
      }),
    [selectedItem],
  );
  useAlicePageContext(aliceContext);

  useEffect(() => {
    if (!communicationId || selectedItem?.recordId === communicationId) return;
    const match = items.find((item) => item.recordId === communicationId);
    if (match) setSelectedItem(match);
  }, [communicationId, items, selectedItem?.recordId]);

  const syncMutation = useMutation({
    mutationFn: () =>
      communicationsApi.syncTaggedGmail({
        search: trimmedSearch || undefined,
        limit: gmailStatus?.limits.maxThreads ?? 5,
      }),
    onSuccess: (result) => {
      setNotice(
        `Gmail sync finished: ${result.created} created, ${result.updated} updated, ${result.skipped} skipped.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['communications'] });
      void queryClient.invalidateQueries({ queryKey: ['communications', 'gmail-status'] });
    },
    onError: (err) => {
      setNotice(err instanceof Error ? err.message : 'Gmail sync failed.', 'destructive');
      void queryClient.invalidateQueries({ queryKey: ['communications', 'gmail-status'] });
    },
  });

  const closeDrawer = () => {
    setSelectedItem(null);
    if (communicationId) {
      const next = new URLSearchParams(searchParams);
      next.delete('communicationId');
      setSearchParams(next, { replace: true });
    }
  };

  const openCommunication = (item: CommunicationTimelineItem) => {
    setSelectedItem(item);
    const next = new URLSearchParams(searchParams);
    next.set('communicationId', item.recordId ?? item.id);
    setSearchParams(next, { replace: false });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MessageSquareText className="h-4 w-4" aria-hidden />
            <span>Communications</span>
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-normal">Client Messages Timeline</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Quick Notes from Airtable, Gmail imports from Supabase, and outgoing client updates in
            one admin timeline.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-9 gap-1.5"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || Boolean(syncBlockedReason)}
            title={syncBlockedReason}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
            )}
            Sync Gmail
          </Button>
          <Badge variant={gmailStatusBadgeVariant(gmailStatus, gmailStatusQuery.isLoading)}>
            {gmailStatusBadgeText(gmailStatus, gmailStatusQuery.isLoading)}
          </Badge>
          <label className="relative min-w-[240px]">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search subject, client, email..."
              className="pl-8"
            />
          </label>
          <Select value={source} onValueChange={(value) => setSource(value as SourceFilter)}>
            <SelectTrigger className="h-9 min-w-[180px]" aria-label="Communication source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading && <Card className="h-[360px] animate-pulse" />}
      {error && <ErrorCard error={error} />}
      {!isLoading && !error && (
        <div
          className={cn(
            'grid gap-4',
            selectedItem && '2xl:grid-cols-[minmax(0,1fr)_minmax(420px,720px)]',
          )}
        >
          <TimelineCard items={items} selectedId={selectedItem?.id} onSelect={openCommunication} />
          {selectedItem && <CommunicationDetailsPanel item={selectedItem} onClose={closeDrawer} />}
        </div>
      )}
    </div>
  );
}

function gmailSyncBlockedReason(
  status: GmailIntegrationStatus | undefined,
  isLoading: boolean,
): string | undefined {
  if (isLoading) return 'Checking Gmail connection status.';
  if (!status?.connected) {
    return status?.reconnectRequired
      ? 'Reconnect Gmail before syncing.'
      : 'Connect Gmail before syncing.';
  }
  if (!status.syncEnabled) return 'Gmail sync is disabled by the Worker kill switch.';
  return undefined;
}

function gmailStatusBadgeVariant(
  status: GmailIntegrationStatus | undefined,
  isLoading: boolean,
): 'success' | 'warning' | 'muted' {
  if (isLoading) return 'muted';
  if (status?.connected && status.syncEnabled) return 'success';
  if (status?.connected || status?.reconnectRequired || status?.error) return 'warning';
  return 'muted';
}

function gmailStatusBadgeText(
  status: GmailIntegrationStatus | undefined,
  isLoading: boolean,
): string {
  if (isLoading) return 'Checking Gmail';
  if (status?.connected) return status.syncEnabled ? 'Gmail ready' : 'Gmail sync off';
  if (status?.reconnectRequired) return 'Reconnect Gmail';
  if (status?.error) return 'Gmail status issue';
  return 'Gmail disconnected';
}

function LinkPicker<T>({
  label,
  value,
  placeholder,
  selected,
  selectedValue,
  options,
  getKey,
  getLabel,
  getMeta,
  onChange,
  onSelect,
}: {
  label: string;
  value: string;
  placeholder: string;
  selected?: string;
  selectedValue?: string;
  options: T[];
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  getMeta: (item: T) => string | undefined;
  onChange: (value: string) => void;
  onSelect: (item: T) => void;
}) {
  const visibleOptions = useMemo(() => options.slice(0, 5), [options]);
  const inputMatchesSelection = Boolean(
    selected &&
    [selected, selectedValue]
      .filter((candidate): candidate is string => Boolean(candidate))
      .some((candidate) => candidate.trim().toLowerCase() === value.trim().toLowerCase()),
  );
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="text-[11px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
          {label}
        </label>
        {selected && <Badge variant="success">{selected}</Badge>}
      </div>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-8 text-[12px]"
      />
      {visibleOptions.length > 0 && !inputMatchesSelection && (
        <div className="mt-1 max-h-36 overflow-y-auto rounded-md border bg-background">
          {visibleOptions.map((option) => (
            <button
              key={getKey(option)}
              type="button"
              className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-[12px] hover:bg-muted"
              onClick={() => onSelect(option)}
            >
              <span className="min-w-0 flex-1 truncate">{getLabel(option)}</span>
              {getMeta(option) && (
                <span className="max-w-[45%] truncate text-[11px] text-muted-foreground">
                  {getMeta(option)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineCard({
  items,
  selectedId,
  onSelect,
}: {
  items: CommunicationTimelineItem[];
  selectedId?: string;
  onSelect: (item: CommunicationTimelineItem) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-border">
        {items.length === 0 && (
          <li className="px-4 py-12 text-center text-sm text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <MessageSquareText className="h-6 w-6 text-muted-foreground/50" aria-hidden />
              <span>No communication items match the current filters.</span>
            </div>
          </li>
        )}
        {items.map((item) => (
          <TimelineItem
            key={item.id}
            item={item}
            selected={item.id === selectedId}
            onSelect={() => onSelect(item)}
          />
        ))}
      </ul>
    </Card>
  );
}

function TimelineItem({
  item,
  selected,
  onSelect,
}: {
  item: CommunicationTimelineItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = sourceIcon(item.source);
  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onSelect();
  };

  return (
    <li
      className={
        selected
          ? 'flex cursor-pointer flex-col gap-3 bg-muted/40 px-4 py-4 md:flex-row md:items-start'
          : 'flex cursor-pointer flex-col gap-3 px-4 py-4 hover:bg-muted/30 md:flex-row md:items-start'
      }
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Review communication: ${item.title}`}
    >
      <div className="flex items-center gap-2 md:w-36 md:flex-none">
        <span className="flex h-8 w-8 items-center justify-center rounded-md border bg-muted/40">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
        </span>
        <Badge variant={sourceBadgeVariant(item.source)}>{item.sourceLabel}</Badge>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
          <h2 className="truncate text-sm font-semibold">{item.title}</h2>
          <time className="shrink-0 text-[11px] text-muted-foreground">
            {formatDateTime(item.occurredAt)}
          </time>
        </div>
        {item.summary && (
          <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
            {item.source === 'gmail' ? cleanEmailSummary(item.summary) : item.summary}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
          {(item.clientName ?? item.clientId) && (
            <span className="inline-flex items-center gap-1">
              <Building2 className="h-3 w-3" aria-hidden />
              {item.clientName ?? item.clientId}
            </span>
          )}
          {(item.userName ?? item.contactEmail) && (
            <span className="inline-flex items-center gap-1">
              <UserRound className="h-3 w-3" aria-hidden />
              {item.userName ?? item.contactEmail}
            </span>
          )}
          {item.status && <span>{formatLabel(item.status)}</span>}
          {item.tags.slice(0, 4).map((tag) => (
            <Badge key={tag} variant="muted">
              {formatLabel(tag)}
            </Badge>
          ))}
        </div>
      </div>
      {item.href && (
        <Button
          asChild
          size="sm"
          variant="outline"
          className="h-8 gap-1.5 self-start"
          onClick={(event) => event.stopPropagation()}
        >
          <a href={item.href} target="_blank" rel="noreferrer">
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            {item.source === 'gmail' ? 'Open Gmail' : 'Open'}
          </a>
        </Button>
      )}
    </li>
  );
}

export function CommunicationDetailsDrawer({
  item,
  onClose,
}: {
  item: CommunicationTimelineItem;
  onClose: () => void;
}) {
  return <CommunicationDetailsSurface item={item} onClose={onClose} surface="drawer" />;
}

export function CommunicationDetailsPanel({
  item,
  onClose,
}: {
  item: CommunicationTimelineItem;
  onClose: () => void;
}) {
  return <CommunicationDetailsSurface item={item} onClose={onClose} surface="panel" />;
}

function CommunicationDetailsSurface({
  item,
  onClose,
  surface,
}: {
  item: CommunicationTimelineItem;
  onClose: () => void;
  surface: 'drawer' | 'panel';
}) {
  const openEmailComposer = useUiStore((state) => state.openEmailComposer);
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const recordId = item.source === 'gmail' ? item.recordId : undefined;
  const { data, isLoading, error } = useQuery({
    queryKey: ['communication-detail', recordId],
    queryFn: () => communicationsApi.get(recordId ?? ''),
    enabled: Boolean(recordId),
  });
  const Icon = sourceIcon(item.source);
  const linkedTaskId =
    item.taskId ?? (data ? suggestedTaskId(data.record.ai_suggested_links) : undefined);
  const suggestedWebsite = data ? suggestedWebsiteId(data.record.ai_suggested_links) : undefined;

  const description = (
    <>
      <Badge variant={sourceBadgeVariant(item.source)}>{item.sourceLabel}</Badge>
      <time>{formatDateTime(item.occurredAt)}</time>
      {(item.clientName ?? item.clientId) && <span>{item.clientName ?? item.clientId}</span>}
      {surface === 'panel' && (
        <Button type="button" size="sm" variant="outline" className="h-7" onClick={onClose}>
          Close
        </Button>
      )}
    </>
  );
  const body = (
    <CommunicationDetailsBody
      item={item}
      data={data}
      isLoading={isLoading}
      error={error}
      recordId={recordId}
      linkedTaskId={linkedTaskId}
      suggestedWebsite={suggestedWebsite}
      openEmailComposer={openEmailComposer}
      openCreateDrawer={openCreateDrawer}
      surface={surface}
    />
  );

  if (surface === 'panel') {
    return (
      <DetailsPanel
        title={item.title}
        icon={Icon}
        description={description}
        className="2xl:sticky 2xl:top-0"
      >
        {body}
      </DetailsPanel>
    );
  }

  return (
    <DetailsDrawer
      title={item.title}
      icon={Icon}
      onClose={onClose}
      widthClassName="sm:max-w-[640px] md:max-w-[720px]"
      description={description}
    >
      {body}
    </DetailsDrawer>
  );
}

function CommunicationDetailsBody({
  item,
  data,
  isLoading,
  error,
  recordId,
  linkedTaskId,
  suggestedWebsite,
  openEmailComposer,
  openCreateDrawer,
  surface,
}: {
  item: CommunicationTimelineItem;
  data?: CommunicationRecordDetails;
  isLoading: boolean;
  error: unknown;
  recordId?: string;
  linkedTaskId?: string;
  suggestedWebsite?: string;
  openEmailComposer: ReturnType<typeof useUiStore.getState>['openEmailComposer'];
  openCreateDrawer: ReturnType<typeof useUiStore.getState>['openCreateDrawer'];
  surface: 'drawer' | 'panel';
}) {
  const Body = surface === 'drawer' ? DetailsDrawerBody : DetailsPanelBody;

  return (
    <Body className="space-y-4">
      {(item.href || (item.source === 'gmail' && data)) && (
        <DetailsActionBar>
          {item.href && (
            <Button asChild type="button" size="sm" variant="outline" className="gap-1.5">
              <a href={item.href} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                {item.source === 'gmail' ? 'Open Gmail' : 'Open source'}
              </a>
            </Button>
          )}
          {item.source === 'gmail' && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() =>
                openEmailComposer({
                  mode: 'reply',
                  replyToRecordId: item.recordId,
                  threadId: asDisplayString(data?.record.gmail_thread_id),
                  subject: item.title,
                  toEmails: item.contactEmail ? [item.contactEmail] : [],
                  clientId: item.clientId,
                  clientName: item.clientName,
                  userId: item.userId,
                  userName: item.userName,
                  taskId: item.taskId,
                })
              }
            >
              <Reply className="h-3.5 w-3.5" aria-hidden />
              Reply
            </Button>
          )}
          {item.source === 'gmail' && data && recordId && !linkedTaskId && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() =>
                openCreateDrawer({
                  type: 'task',
                  values: {
                    name: `Follow up: ${asDisplayString(data.record.subject) ?? item.title}`.slice(
                      0,
                      500,
                    ),
                    notes: buildEmailTaskNotes(item, data.record),
                    status: 'Open',
                    clientId:
                      item.clientId ??
                      asDisplayString(data.record.linked_airtable_organization_id),
                    clientName:
                      item.clientName ??
                      item.clientId ??
                      asDisplayString(data.record.linked_airtable_organization_id),
                    websiteId: suggestedWebsite ?? item.websiteId,
                    websiteName: suggestedWebsite ?? item.websiteId,
                    source: 'gmail',
                    taskType: 'email_follow_up',
                    sourceLabel: 'Gmail',
                    sourceTitle: item.title,
                    gmailRecordId: recordId,
                    gmailUserId:
                      item.userId ?? asDisplayString(data.record.linked_airtable_user_id),
                  },
                })
              }
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Create Task From Email
            </Button>
          )}
        </DetailsActionBar>
      )}

      {isLoading && <div className="text-sm text-muted-foreground">Loading details...</div>}
      {Boolean(error) && (
        <div className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not load details.'}
        </div>
      )}
      {data && item.source === 'gmail' && <GmailDetailsTabs item={item} details={data} />}
      {item.source !== 'gmail' && (
        <>
          <DetailGroup title="Linked Records">
            <DetailRow label="Org" value={item.clientName ?? item.clientId} />
            <DetailRow label="User/email" value={item.userName ?? item.contactEmail} />
            <DetailRow label="Task" value={item.taskId} />
            <DetailRow
              label="Status"
              value={item.status ? formatLabel(item.status) : undefined}
            />
          </DetailGroup>

          {item.summary && (
            <DetailGroup title="Summary">
              <ReadableSummary value={item.summary} />
            </DetailGroup>
          )}

          <DetailGroup title="Source Metadata">
            <JsonPreview value={item.metadata} />
          </DetailGroup>
        </>
      )}
    </Body>
  );
}

function GmailDetailsTabs({
  item,
  details,
}: {
  item: CommunicationTimelineItem;
  details: CommunicationRecordDetails;
}) {
  const [tab, setTab] = useState('thread');
  const [editingLinks, setEditingLinks] = useState(false);
  const record = details.record;
  const recordId = asDisplayString(record.id);
  const summary = asDisplayString(record.summary) ?? item.summary;
  const threadQuery = useQuery({
    queryKey: ['communication-thread', recordId],
    queryFn: () => communicationsApi.getThread(recordId ?? ''),
    enabled: Boolean(recordId) && tab === 'thread',
    staleTime: 60_000,
  });

  return (
    <Tabs value={tab} onValueChange={setTab} className="space-y-3">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="thread">Thread</TabsTrigger>
        <TabsTrigger value="links">Links</TabsTrigger>
        <TabsTrigger value="events">Events</TabsTrigger>
        <TabsTrigger value="metadata">Metadata</TabsTrigger>
      </TabsList>

      <TabsContent value="thread" className="space-y-3">
        {summary && (
          <DetailGroup title="AI Summary">
            <ReadableSummary value={summary} />
          </DetailGroup>
        )}
        <DetailGroup title="Subject">
          <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm font-medium">
            {asDisplayString(record.subject) ?? item.title}
          </div>
        </DetailGroup>
        {threadQuery.isLoading && (
          <div className="text-sm text-muted-foreground">Loading Gmail thread...</div>
        )}
        {threadQuery.error && (
          <div className="text-sm text-destructive">
            {threadQuery.error instanceof Error
              ? threadQuery.error.message
              : 'Could not load Gmail thread.'}
          </div>
        )}
        {threadQuery.data && <MessageAccordionList messages={threadQuery.data.messages} />}
      </TabsContent>

      <TabsContent value="links" className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <LinkedPills item={item} record={record} />
          <Button
            type="button"
            size="sm"
            variant={editingLinks ? 'secondary' : 'outline'}
            className="h-8 gap-1.5"
            onClick={() => setEditingLinks((value) => !value)}
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            {editingLinks ? 'Hide Editor' : 'Edit Link'}
          </Button>
        </div>
        {editingLinks && recordId && (
          <CommunicationLinkEditor
            recordId={recordId}
            item={item}
            record={record}
            onSaved={() => setEditingLinks(false)}
          />
        )}
      </TabsContent>

      <TabsContent value="events" className="space-y-3">
        <EventList events={details.events} />
      </TabsContent>

      <TabsContent value="metadata" className="space-y-3">
        <DetailGroup title="Gmail">
          <DetailRow label="Thread" value={asDisplayString(record.gmail_thread_id)} />
          <DetailRow label="Messages" value={formatCount(record.message_count)} />
          <DetailRow label="From" value={asDisplayString(record.from_email)} />
          <DetailRow label="To" value={asDisplayArray(record.to_emails)} />
          <DetailRow label="CC" value={asDisplayArray(record.cc_emails)} />
          <DetailRow label="URL" value={asDisplayString(record.gmail_url)} />
        </DetailGroup>
        <DetailGroup title="Source metadata">
          <JsonPreview value={record} />
        </DetailGroup>
      </TabsContent>
    </Tabs>
  );
}

function MessageAccordionList({
  messages,
}: {
  messages: Array<{
    id: string;
    from?: { name?: string; email: string };
    to: Array<{ name?: string; email: string }>;
    cc: Array<{ name?: string; email: string }>;
    date?: string;
    bodyText: string;
    snippet?: string;
  }>;
}) {
  if (messages.length === 0) {
    return (
      <Card className="px-3 py-3 text-sm text-muted-foreground">
        No Gmail messages were returned for this thread.
      </Card>
    );
  }
  const latestId = messages[messages.length - 1]?.id;
  return (
    <div className="space-y-2">
      {messages.map((message) => (
        <details
          key={message.id}
          className="rounded-md border bg-background"
          open={message.id === latestId}
        >
          <summary className="cursor-pointer px-3 py-2">
            <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">
                {message.from?.name ?? message.from?.email ?? 'Unknown sender'}
              </span>
              {message.date && (
                <time className="text-[11px] text-muted-foreground">
                  {formatDateTime(message.date)}
                </time>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
              {message.to.length > 0 && <span>To {formatPeople(message.to)}</span>}
              {message.cc.length > 0 && <span>CC {formatPeople(message.cc)}</span>}
            </div>
          </summary>
          <div className="border-t px-3 py-3">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
              {message.bodyText || message.snippet || 'No readable body text.'}
            </p>
          </div>
        </details>
      ))}
    </div>
  );
}

function LinkedPills({
  item,
  record,
}: {
  item: CommunicationTimelineItem;
  record: Record<string, unknown>;
}) {
  const websiteId = suggestedWebsiteId(record.ai_suggested_links);
  const taskId = item.taskId ?? suggestedTaskId(record.ai_suggested_links);
  const pills = [
    (item.clientName ?? item.clientId)
      ? { label: 'Client', value: item.clientName ?? item.clientId }
      : undefined,
    (item.userName ?? item.contactEmail)
      ? { label: 'Contact', value: item.userName ?? item.contactEmail }
      : undefined,
    taskId ? { label: 'Task', value: taskId, taskId } : undefined,
    websiteId ? { label: 'Website', value: websiteId } : undefined,
  ].filter((pill): pill is { label: string; value: string; taskId?: string } =>
    Boolean(pill?.value),
  );

  if (pills.length === 0) {
    return <span className="text-sm text-muted-foreground">No links selected.</span>;
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {pills.map((pill) => (
        <Badge key={`${pill.label}:${pill.value}`} variant="success">
          {pill.label}: {pill.taskId ? <TaskName taskId={pill.taskId} /> : pill.value}
        </Badge>
      ))}
    </div>
  );
}

function TaskName({ taskId }: { taskId: string }) {
  const taskQuery = useQuery({
    queryKey: ['communications', 'task-name', taskId],
    queryFn: () => tasksApi.get(taskId),
    enabled: Boolean(taskId),
    staleTime: 5 * 60_000,
  });

  return <>{taskQuery.data?.name ?? taskId}</>;
}

function CommunicationLinkEditor({
  recordId,
  item,
  record,
  onSaved,
}: {
  recordId: string;
  item: CommunicationTimelineItem;
  record: Record<string, unknown>;
  onSaved: () => void;
}) {
  const queryClient = useQueryClient();
  const suggestedEmail =
    asDisplayString(record.matched_contact_email) ??
    item.contactEmail ??
    asDisplayString(record.from_email) ??
    '';
  const linkedTaskId = item.taskId ?? suggestedTaskId(record.ai_suggested_links);
  const [clientSearch, setClientSearch] = useState(item.clientName ?? item.clientId ?? '');
  const [contactSearch, setContactSearch] = useState(item.userName ?? suggestedEmail);
  const [taskSearch, setTaskSearch] = useState(linkedTaskId ?? '');
  const [websiteSearch, setWebsiteSearch] = useState(
    suggestedWebsiteId(record.ai_suggested_links) ?? item.websiteId ?? '',
  );
  const [selectedClient, setSelectedClient] = useState<Client | undefined>(
    item.clientId
      ? ({ id: item.clientId, name: item.clientName ?? item.clientId } as Client)
      : undefined,
  );
  const [selectedContact, setSelectedContact] = useState<UserEntity | undefined>(
    item.userId
      ? ({
          id: item.userId,
          fullName: item.userName ?? item.contactEmail ?? item.userId,
          email: item.contactEmail,
        } as UserEntity)
      : undefined,
  );
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(
    linkedTaskId ? ({ id: linkedTaskId, name: linkedTaskId } as Task) : undefined,
  );
  const [selectedWebsite, setSelectedWebsite] = useState<Website | undefined>(
    suggestedWebsiteId(record.ai_suggested_links) || item.websiteId
      ? ({
          id: suggestedWebsiteId(record.ai_suggested_links) ?? item.websiteId,
          url: websiteSearch,
        } as Website)
      : undefined,
  );

  const clientsQuery = useQuery({
    queryKey: ['communications', 'link-clients', clientSearch],
    queryFn: () => clientsApi.list({ q: clientSearch.trim() || undefined }),
    enabled: clientSearch.trim().length > 1,
    staleTime: 60_000,
  });
  const contactsQuery = useQuery({
    queryKey: ['communications', 'link-contacts', contactSearch, selectedClient?.id],
    queryFn: () =>
      usersApi.list({
        q: contactSearch.trim() || undefined,
        clientId: selectedClient?.id,
      }),
    enabled: contactSearch.trim().length > 1 || Boolean(selectedClient?.id),
    staleTime: 60_000,
  });
  const tasksQuery = useQuery({
    queryKey: ['communications', 'link-tasks', selectedClient?.id, taskSearch],
    queryFn: () =>
      tasksApi.list({
        clientId: selectedClient?.id,
        search: taskSearch.trim() || undefined,
        includeClosed: false,
        limit: 25,
      }),
    enabled: Boolean(selectedClient?.id) || taskSearch.trim().length > 1,
    staleTime: 60_000,
  });
  const linkedTaskQuery = useQuery({
    queryKey: ['communications', 'linked-task', linkedTaskId],
    queryFn: () => tasksApi.get(linkedTaskId ?? ''),
    enabled: Boolean(linkedTaskId),
    staleTime: 5 * 60_000,
  });
  const websitesQuery = useQuery({
    queryKey: ['communications', 'link-websites', selectedClient?.id, websiteSearch],
    queryFn: () =>
      websitesApi.list({
        clientId: selectedClient?.id,
        q: websiteSearch.trim() || undefined,
      }),
    enabled: Boolean(selectedClient?.id) || websiteSearch.trim().length > 1,
    staleTime: 60_000,
  });

  useEffect(() => {
    const task = linkedTaskQuery.data;
    if (!task) return;
    setSelectedTask(task);
    setTaskSearch(task.name);
    if (task.client && !selectedClient) {
      setSelectedClient(task.client as Client);
      setClientSearch(task.client.name ?? task.client.id ?? '');
    }
  }, [linkedTaskQuery.data, selectedClient]);

  const mutation = useMutation({
    mutationFn: () =>
      communicationsApi.patchLinks(recordId, {
        clientId: selectedClient?.id ?? null,
        userId: selectedContact?.id ?? null,
        taskId: selectedTask?.id ?? null,
        websiteId: selectedWebsite?.id ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['communications'] });
      void queryClient.invalidateQueries({ queryKey: ['communication-detail', recordId] });
      onSaved();
    },
  });

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] font-semibold text-foreground">Link records</div>
        {suggestedEmail && <Badge variant="muted">Suggested from {suggestedEmail}</Badge>}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <LinkPicker
          label="Client"
          value={clientSearch}
          placeholder="Search organization"
          selected={selectedClient?.name}
          selectedValue={selectedClient?.name}
          options={clientsQuery.data?.items ?? []}
          getKey={(client) => client.id}
          getLabel={(client) => client.name}
          getMeta={(client) => client.status}
          onChange={(value) => {
            setClientSearch(value);
            if (!value.trim()) setSelectedClient(undefined);
          }}
          onSelect={(client) => {
            setSelectedClient(client);
            setClientSearch(client.name);
            setSelectedTask(undefined);
          }}
        />
        <LinkPicker
          label="Contact"
          value={contactSearch}
          placeholder="Search name or email"
          selected={selectedContact?.fullName}
          selectedValue={selectedContact?.email ?? selectedContact?.fullName}
          options={contactsQuery.data?.items ?? []}
          getKey={(contact) => contact.id}
          getLabel={(contact) => contact.fullName}
          getMeta={(contact) => contact.email}
          onChange={(value) => {
            setContactSearch(value);
            if (!value.trim()) setSelectedContact(undefined);
          }}
          onSelect={(contact) => {
            setSelectedContact(contact);
            setContactSearch(contact.email ?? contact.fullName);
            const firstClientId = contact.clientIds?.[0] ?? contact.clientRecordIds?.[0];
            if (firstClientId && !selectedClient) {
              setSelectedClient({ id: firstClientId, name: firstClientId } as Client);
              setClientSearch(firstClientId);
            }
          }}
        />
        <LinkPicker
          label="Task"
          value={taskSearch}
          placeholder={selectedClient ? 'Search open tasks' : 'Pick client or search task'}
          selected={selectedTask?.name}
          selectedValue={selectedTask?.name}
          options={tasksQuery.data?.items ?? []}
          getKey={(task) => task.id}
          getLabel={(task) => task.name}
          getMeta={(task) => task.client?.name ?? task.status}
          onChange={(value) => {
            setTaskSearch(value);
            if (!value.trim()) setSelectedTask(undefined);
          }}
          onSelect={(task) => {
            setSelectedTask(task);
            setTaskSearch(task.name);
            if (task.client && !selectedClient) {
              setSelectedClient(task.client as Client);
              setClientSearch(task.client.name ?? task.client.id ?? '');
            }
          }}
        />
        <LinkPicker
          label="Website"
          value={websiteSearch}
          placeholder={selectedClient ? 'Search websites' : 'Pick client or search website'}
          selected={selectedWebsite?.friendlyName ?? selectedWebsite?.url}
          selectedValue={selectedWebsite?.friendlyName ?? selectedWebsite?.url}
          options={websitesQuery.data?.items ?? []}
          getKey={(website) => website.id}
          getLabel={(website) => website.friendlyName ?? website.url}
          getMeta={(website) => website.organizationName ?? website.status}
          onChange={(value) => {
            setWebsiteSearch(value);
            if (!value.trim()) setSelectedWebsite(undefined);
          }}
          onSelect={(website) => {
            setSelectedWebsite(website);
            setWebsiteSearch(website.friendlyName ?? website.url);
          }}
        />
      </div>
      {mutation.error && (
        <div className="mt-2 text-[12px] text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : 'Link update failed'}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Link2 className="h-3.5 w-3.5" aria-hidden />
          )}
          Save Link
        </Button>
      </div>
    </div>
  );
}

function EventList({ events }: { events: Array<Record<string, unknown>> }) {
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">No events recorded.</p>;
  }
  return (
    <div className="space-y-2">
      {events.slice(0, 12).map((event, index) => (
        <div key={String(event.id ?? index)} className="rounded-md border px-3 py-2">
          <div className="text-sm font-medium">
            {formatLabel(asDisplayString(event.event_type) ?? 'event')}
          </div>
          {typeof event.event_summary === 'string' && event.event_summary.trim() && (
            <div className="mt-0.5 text-[12px] text-muted-foreground">{event.event_summary}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ReadableSummary({ value }: { value: string }) {
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const title = lines.find((line) => line.startsWith('# '))?.replace(/^#\s+/, '');
  const bullets = lines.filter((line) => line.startsWith('- ')).map((line) => line.slice(2));
  const quotes = lines
    .filter((line) => line.startsWith('>'))
    .map((line) => line.replace(/^>\s*/, ''));
  return (
    <div className="space-y-2">
      {title && <div className="text-sm font-semibold text-foreground">{title}</div>}
      {bullets.length > 0 && (
        <div className="grid gap-1 text-[12px] text-muted-foreground">
          {bullets.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}
      {quotes.length > 0 && (
        <div className="space-y-1">
          {quotes.map((line) => (
            <p
              key={line}
              className="rounded-md border bg-muted/20 px-3 py-2 text-sm text-muted-foreground"
            >
              {line}
            </p>
          ))}
        </div>
      )}
      {!title && bullets.length === 0 && quotes.length === 0 && (
        <p className="whitespace-pre-wrap text-sm text-muted-foreground">{value}</p>
      )}
    </div>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" aria-hidden />
        <h2 className="text-base font-semibold">Couldn't load communications</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {error instanceof Error ? error.message : 'Unknown error'}
      </p>
    </Card>
  );
}

function sourceIcon(source: CommunicationTimelineSource) {
  if (source === 'gmail') return Mail;
  if (source === 'quick_note') return NotebookPen;
  return Activity;
}

function sourceBadgeVariant(
  source: CommunicationTimelineSource,
): 'success' | 'warning' | 'destructive' | 'muted' {
  if (source === 'gmail') return 'success';
  if (source === 'quick_note') return 'warning';
  return 'muted';
}

function formatLabel(value: string): string {
  return value.replaceAll('_', ' ');
}

function cleanEmailSummary(value: string): string {
  return value
    .replace(/^#\s+/gm, '')
    .replace(/^- Gmail thread:.*$/gm, '')
    .replace(/^- Messages:.*$/gm, '')
    .replace(/^- Last message:.*$/gm, '')
    .replace(/^- Participants:.*$/gm, '')
    .replace(/^>\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildEmailTaskNotes(
  item: CommunicationTimelineItem,
  record: Record<string, unknown>,
): string {
  const subject = asDisplayString(record.subject) ?? item.title;
  const summarySource =
    asDisplayString(record.summary) ?? item.summary ?? asDisplayString(record.raw_excerpt);
  const summary = summarySource ? cleanEmailSummary(summarySource) : undefined;
  const lines = [`Created from Gmail communication: ${subject}`];
  const from = asDisplayString(record.from_email);
  const to = asDisplayArray(record.to_emails);
  const cc = asDisplayArray(record.cc_emails);
  const gmailUrl = asDisplayString(record.gmail_url) ?? item.href;
  const lastMessageAt = asDisplayString(record.last_message_at) ?? item.occurredAt;

  if (summary) lines.push(`Email summary:\n${summary}`);
  if (from) lines.push(`From: ${from}`);
  if (to) lines.push(`To: ${to}`);
  if (cc) lines.push(`CC: ${cc}`);
  if (lastMessageAt) lines.push(`Last message: ${formatDateTime(lastMessageAt)}`);
  if (gmailUrl) lines.push(`Gmail thread: ${gmailUrl}`);

  return lines.join('\n\n');
}

function formatPeople(people: Array<{ name?: string; email: string }>): string {
  return people.map((person) => person.name ?? person.email).join(', ');
}

function suggestedWebsiteId(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const links = value as Record<string, unknown>;
  if (typeof links.primaryWebsiteId === 'string' && links.primaryWebsiteId.trim()) {
    return links.primaryWebsiteId;
  }
  if (Array.isArray(links.airtableWebsiteIds) && typeof links.airtableWebsiteIds[0] === 'string') {
    return links.airtableWebsiteIds[0];
  }
  return undefined;
}

function suggestedTaskId(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const links = value as Record<string, unknown>;
  if (typeof links.primaryTaskId === 'string' && links.primaryTaskId.trim()) {
    return links.primaryTaskId;
  }
  if (Array.isArray(links.airtableTaskIds) && typeof links.airtableTaskIds[0] === 'string') {
    return links.airtableTaskIds[0];
  }
  return undefined;
}

function formatDateTime(value: string): string {
  const time = Date.parse(value);
  if (Number.isNaN(time)) return value;
  return new Date(time).toLocaleString();
}

function asDisplayString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asDisplayArray(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;
  const values = value.filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0,
  );
  return values.length ? values.join(', ') : undefined;
}

function formatCount(value: unknown): string | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : undefined;
}
